import { meetingTypesArray } from '../shared/meetingTypeToTranslationKey';
import { getStatusesRaw } from '../shared/orderStatusToTranslationKey';
import { getStatusesRaw as getPaymentStatus } from '../shared/paymentStatusToTranslationKey';
import { unitsArray } from '../shared/unitsToTranslationKey';
import polishFlag from '../assets/images/polish-flag.png';
import americanFlag from '../assets/images/american-flag.png';
import germanFlag from '../assets/images/german-flag.webp';
import spanishFlag from '../assets/images/spanish-flag.png';
import italianFlag from '../assets/images/italian-flag.png';
import ukranianFlag from '../assets/images/ukranian-flag.svg';
import portugueseFlag from '../assets/images/portuguese-flag.jpeg';
import { getExternalStatusesRaw } from '../shared/externalOrderStatusToTranslationKey';
import { PaymentStatus } from '../types/payment-statuses';

const CONFIG = {
    VERSION: '9.2.0',
    MAIN_WEBSITE: 'https://eateliers.com',
    ROLES: ['office', 'shop', 'manager'],
    MOBILE_BREAKPOINT: 768,
    API_ENDPOINT: 'https://api.eateliers.com',
    // API_ENDPOINT: 'http://localhost:80',
    GEMINI_API_KEY: 'AIzaSyDHj9ja4x5wg3aveFQlD_XPgM0Sibt1Q8A',
    NAME: 'Demo',
    AMPLITUDE_ENABLED: true as boolean, // set false to disable init + all events
    // AMPLITUDE: '92f04cd4d8e9b4a5b4d382d52920432f', // Test
    AMPLITUDE: 'b6c27141be0227d3497995ea1be4d8d5', // Prod
    /** Public Crisp chatbox website ID (client snippet). */
    CRISP_WEBSITE_ID: '58e84036-e8e5-4c58-921e-6fc86c9895ff',
    DEMO_URL: 'https://calendly.com/eateliers',
    FILE_TYPES: ['mp4', 'png', 'jpg', 'jpeg', 'webp', 'wav', 'pdf', 'doc', 'docx', 'heic', 'mov', 'txt', 'odt'],
    MAX_FILE_UPLOAD: 35, // 35MB
    PUBLIC_MAX_FILE_UPLOAD: 5,
    DEFAULT_PRIMARY_COLOR: '#f9c2c2',
    DEFAULT_BACKGROUND_COLOR: '#fef7f7',
    HEAVY_FILE_THRESHOLD: 10, // 10MB - given by AWS
    PUBLIC_HEAVY_FILE_THRESHOLD: 10,
    SHOULD_BE_PRECOMPRESSED: 2,
    VIDEO_TYPES: ['mp4', 'wav', 'avi', 'webm', 'mp4', 'tiff', 'gif'],
    TEXT_TYPES: ['doc', 'docx', 'txt', 'pdf'],
    NOTIFICATION_DECAY: 5000,
    IMAGE_TYPES: ['png', 'jpg', 'jpeg', 'webp'],
    CATALOG_ITEM_TYPES: ['png', 'jpg', 'jpeg', 'webp', 'mp4', 'wav', 'avi', 'webm', 'mp4', 'tiff', 'gif'],

    DEFAULT_PRODUCT_TYPE: "dresses",
    CONTACT_INFO: {
      EMAIL: 'hello@eateliers.com',
      INSTAGRAM: 'https://instagram.com/eateliers',
      WHATSAPP: 'https://wa.me/48732040821',
      PHONE: '+48 732 040 821'
    },
    ORDERS: {
      COMPLETED: ['SENT'],
      PAYMENT_STATUS: getPaymentStatus(),
      STATUS: getStatusesRaw(),
      DEFAULT_STATUS: 'WAITING',
      DEFAULT_PAYMENT_STATUS: PaymentStatus.UNKNOWN
    },
    EXTERNAL_ORDERS: {
      STATUS: getExternalStatusesRaw(),
      DEFAULT_STATUS: 'DRAFT',
    },
    MEETINGS: {
      STATUS: meetingTypesArray,
      REMINDER_ENABLED_TYPES: [
        'VISIT',
        'FIRST_MEASUREMENT', 
        'SECOND_MEASUREMENT',
        'CONTRACT',
        'RECEIVING',
        'TAKING_MEASUREMENTS',
        'SECOND_MEASUREMENT_AND_RECEIVING',
        'SECOND_VISIT',
        'THIRD_VISIT',
        'CONSULTING_SERVICES',
        'OTHER'
      ],
      RESERVATION_TAB_EXCLUDED_TYPES: [
        'NOTE',
        'WAITING_LIST'
      ]
    },
    LOCAL_SESSION: {
      FLAGS_FIELD: 'flags',
      ROLE_FIELD: 'role',
      TOKEN: 'jwtToken',
    },
    PRODUCTION_PLAN: {
      MATERIALS: {
        TYPES: unitsArray
      }
    },
    DEFAULT_LANGUAGE: { code: 'en', icon: americanFlag },
    SUPPORTED_LANGUAGES: [
      { code: 'en', icon: americanFlag },
      { code: 'pl', icon: polishFlag },
      { code: 'de', icon: germanFlag },
      { code: 'uk', icon: ukranianFlag },
      { code: 'es', icon: spanishFlag },
      { code: 'it', icon: italianFlag },
      { code: 'pt', icon: portugueseFlag },
    ],
    SUPPORTED_CURRENCIES: ['EUR', 'USD', 'PLN', 'GBP', 'UAH', 'CHF', 'DKK', 'AUD', 'CAD', 'NOK', 'ILS', 'SGD', 'KRW'],
    DEFAULT_CURRENCY: 'EUR',
    SUPPORTED_CURRENCIES_SYMBOLS: {
      "EUR": "€",
      "USD": "$",
      "PLN": "zł",
      "GBP": "£",
      "CHF": "CHF",
      "DKK": "kr",
      "AUD": "A$",
      "CAD": "C$",
      "NOK": "kr",
      "ILS": "₪",
      "SGD": "S$",
      "KRW": "₩",
      "UAH": "₴"
    },
    NOTIFICATION_CHANNELS: ['sms', 'email'],
    PERMISSIONS: [
      {
        SERVICE: 'CALENDAR',
        ACTIONS: [
          {
            key: "permissions.see_meetings",
            flags: ["see-meetings"]
          },
          {
            key: "permissions.create_meetings",
            flags: ['create-meetings']
          },
          {
            key: "permissions.remove_meetings",
            flags: ['remove-meetings']
          },
          {
            key: "permissions.print_meetings",
            flags: ['print-meetings']
          }
        ]
      },
      {
        SERVICE: 'CONTRACTS',
        ACTIONS: [
          {
            key: "permissions.see_contracts",
            flags: ['see-contracts']
          },
          {
            key: "permissions.edit_contract_template",
            flags: ['edit-contract-template']
          },
          {
            key: "permissions.sign_contracts",
            flags: ['sign-contracts']
          }
        ]
      },
      {
        SERVICE: 'CUSTOMERS',
        ACTIONS: [
          {
            key: "permissions.see_customers",
            flags: ['see-customers']
          },
          {
            key: "permissions.edit_customers",
            flags: ['edit-customers']
          },
          {
            key: "permissions.remove_customers",
            flags: ['remove-customers']
          }
        ]
      },
      {
        SERVICE: 'ORDERS',
        ACTIONS: [
          {
            key: "permissions.see_orders",
            flags: ['see-orders']
          },
          {
            key: "permissions.see_incoming_orders",
            flags: ['see-incoming-orders']
          },
          {
            key: "permissions.see_hidden_orders",
            flags: ['see-hidden-orders']
          },
          {
            key: "permissions.see_high_priority_orders",
            flags: ['see-high-priority-orders']
          },
          {
            key: "permissions.see_self_orders",
            description: "permissions.see_self_orders.description",
            flags: ['see-self-orders']
          },
          {
            key: "permissions.see_files",
            description: "permissions.see_files.description",
            flags: ['see-files']
          },
          {
            key: "permissions.see_orders_contracts",
            description: "permissions.see_orders_contracts.description",
            flags: ['see-orders-contracts']
          },
          {
            key: "permissions.create_orders",
            flags: ["order-add"]
          },
          {
            key: "permissions.remove_orders",
            flags: ['remove-orders']
          },
          {
            key: "permissions.hide_orders",
            flags: ['hide-orders']
          },
          {
            key: "permissions.set_high_priority_orders",
            flags: ['set-high-priority-orders']
          },
          {
            key: "permissions.set_pending_measures_orders",
            flags: ['set-pending-measures-orders']
          },
          {
            key: "permissions.update_orders",
            flags: ['update-orders']
          },
          {
            key: "permissions.print_orders",
            flags: ['print-orders']
          },
          {
            key: "permissions.see_order_story",
            flags: ['order-story-read']
          },
          {
            key: "permissions.write_order_story",
            flags: ['order-story-write']
          },
        ]
      },
      {
        SERVICE: 'MARKETING',
        ACTIONS: [
          {
            key: "permissions.see_marketing_collections",
            flags: ['see-marketing-collections']
          },
          {
            key: "permissions.create_marketing_collections",
            flags: ['create-marketing-collections']
          },
          {
            key: "permissions.update_marketing_collections",
            flags: ['update-marketing-collections']
          },
          {
            key: "permissions.remove_marketing_collections",
            flags: ['remove-marketing-collections']
          },
          {
            key: "permissions.update_marketing_collection_items_prices",
            flags: ['update-marketing-collection-items-prices']
          },
          {
            key: "permissions.update_marketing_collection_items_costs",
            flags: ['update-marketing-collection-items-costs']
          }
        ]
      },
      {
        SERVICE: 'LEADS',
        ACTIONS: [
          {
            key: "permissions.see_leads",
            flags: ['see-leads']
          },
        ]
      },
      {
        SERVICE: 'PARTNERS',
        ACTIONS: [
          {
            key: "permissions.see_partners",
            flags: ['see-partners']
          },
          {
            key: "permissions.create_partners",
            flags: ['create-partners']
          },
          {
            key: "permissions.update_partners",
            flags: ['update-partners']
          },
          {
            key: "permissions.remove_partners",
            flags: ['remove-partners']
          }
        ]
      },
      {
        SERVICE: 'DRESSES',
        ACTIONS: [
          {
            key: "permissions.see_dresses",
            flags: ['see-dresses']
          },
          {
            key: "permissions.create_dresses",
            flags: ['create-dresses']
          },
          {
            key: "permissions.update_dresses",
            flags: ['update-dresses']
          },
          {
            key: "permissions.remove_dresses",
            flags: ['remove-dresses']
          },
          {
            key: "permissions.update_dresses_prices",
            flags: ['update-dresses-prices']
          },
          {
            key: "permissions.update_dresses_costs",
            flags: ['update-dresses-costs']
          }
        ]
      },
      {
        SERVICE: 'PROVIDERS',
        ACTIONS: [
          {
            key: "permissions.see_providers",
            flags: ['see-providers']
          },
          {
            key: "permissions.create_providers",
            flags: ['create-providers']
          },
          {
            key: "permissions.update_providers",
            flags: ['update-providers']
          },
          {
            key: "permissions.remove_providers",
            flags: ['remove-providers']
          }
        ]
      },
      {
        SERVICE: 'COMMISSION_CONTRACTS',
        ACTIONS: [
          {
            key: "permissions.see_commission_contracts",
            flags: ['see-commission-contracts']
          },
          {
            key: "permissions.edit_commission_contracts",
            flags: ['edit-commission-contracts']
          },
          {
            key: "permissions.delete_commission_contracts",
            flags: ['delete-commission-contracts']
          }
        ]
      },
      {
        SERVICE: 'STOCK',
        ACTIONS: [
          {
            key: "permissions.see_stock",
            flags: ['see-stock']
          },
          {
            key: "permissions.update_stock",
            flags: ['update-stock']
          }
        ]
      },
      { SERVICE: 'BACK_OFFICE',
        ACTIONS: [
          {
            key: "permissions.see_back_office",
            flags: ['see-employees']
          },
        ]
      },
      {
        SERVICE: 'EMPLOYEES',
        ACTIONS: [
          {
            key: "permissions.create_employees",
            flags: ['create-employees']
          },
          {
            key: "permissions.update_employees",
            flags: ['update-employees']
          },
          {
            key: "permissions.remove_employees",
            flags: ['remove-employees']
          },
          {
            key: "permissions.see_employee_salaries",
            flags: ["see-employee-salaries"]
          },
          {
            key: "permissions.update_employee_salaries",
            flags: ["update-employee-salaries"]
          }
        ]
      },
    ],
    COUNTRY_CODES: [
      { "countryCode": "AF", "countryName": "afghanistan", "prefix": "+93", "flag": "🇦🇫" },
      { "countryCode": "AL", "countryName": "albania", "prefix": "+355", "flag": "🇦🇱" },
      { "countryCode": "DZ", "countryName": "algeria", "prefix": "+213", "flag": "🇩🇿" },
      { "countryCode": "AS", "countryName": "american_samoa", "prefix": "+1-684", "flag": "🇦🇸" },
      { "countryCode": "AD", "countryName": "andorra", "prefix": "+376", "flag": "🇦🇩" },
      { "countryCode": "AO", "countryName": "angola", "prefix": "+244", "flag": "🇦🇴" },
      { "countryCode": "AI", "countryName": "anguilla", "prefix": "+1-264", "flag": "🇦🇮" },
      { "countryCode": "AG", "countryName": "antigua_and_barbuda", "prefix": "+1-268", "flag": "🇦🇬" },
      { "countryCode": "AR", "countryName": "argentina", "prefix": "+54", "flag": "🇦🇷" },
      { "countryCode": "AM", "countryName": "armenia", "prefix": "+374", "flag": "🇦🇲" },
      { "countryCode": "AW", "countryName": "aruba", "prefix": "+297", "flag": "🇦🇼" },
      { "countryCode": "AU", "countryName": "australia", "prefix": "+61", "flag": "🇦🇺" },
      { "countryCode": "AT", "countryName": "austria", "prefix": "+43", "flag": "🇦🇹" },
      { "countryCode": "AZ", "countryName": "azerbaijan", "prefix": "+994", "flag": "🇦🇿" },
      { "countryCode": "BS", "countryName": "bahamas", "prefix": "+1-242", "flag": "🇧🇸" },
      { "countryCode": "BH", "countryName": "bahrain", "prefix": "+973", "flag": "🇧🇭" },
      { "countryCode": "BD", "countryName": "bangladesh", "prefix": "+880", "flag": "🇧🇩" },
      { "countryCode": "BB", "countryName": "barbados", "prefix": "+1-246", "flag": "🇧🇧" },
      { "countryCode": "BY", "countryName": "belarus", "prefix": "+375", "flag": "🇧🇾" },
      { "countryCode": "BE", "countryName": "belgium", "prefix": "+32", "flag": "🇧🇪" },
      { "countryCode": "BZ", "countryName": "belize", "prefix": "+501", "flag": "🇧🇿" },
      { "countryCode": "BJ", "countryName": "benin", "prefix": "+229", "flag": "🇧🇯" },
      { "countryCode": "BM", "countryName": "bermuda", "prefix": "+1-441", "flag": "🇧🇲" },
      { "countryCode": "BT", "countryName": "bhutan", "prefix": "+975", "flag": "🇧🇹" },
      { "countryCode": "BO", "countryName": "bolivia", "prefix": "+591", "flag": "🇧🇴" },
      { "countryCode": "BA", "countryName": "bosnia_and_herzegovina", "prefix": "+387", "flag": "🇧🇦" },
      { "countryCode": "BW", "countryName": "botswana", "prefix": "+267", "flag": "🇧🇼" },
      { "countryCode": "BR", "countryName": "brazil", "prefix": "+55", "flag": "🇧🇷" },
      { "countryCode": "BN", "countryName": "brunei_darussalam", "prefix": "+673", "flag": "🇧🇳" },
      { "countryCode": "BG", "countryName": "bulgaria", "prefix": "+359", "flag": "🇧🇬" },
      { "countryCode": "BF", "countryName": "burkina_faso", "prefix": "+226", "flag": "🇧🇫" },
      { "countryCode": "BI", "countryName": "burundi", "prefix": "+257", "flag": "🇧🇮" },
      { "countryCode": "KH", "countryName": "cambodia", "prefix": "+855", "flag": "🇰🇭" },
      { "countryCode": "CM", "countryName": "cameroon", "prefix": "+237", "flag": "🇨🇲" },
      { "countryCode": "CA", "countryName": "canada", "prefix": "+1", "flag": "🇨🇦" },
      { "countryCode": "CV", "countryName": "cape_verde", "prefix": "+238", "flag": "🇨🇻" },
      { "countryCode": "CF", "countryName": "central_african_republic", "prefix": "+236", "flag": "🇨🇫" },

      { "countryCode": "TD", "countryName": "chad", "prefix": "+235", "flag": "🇹🇩" },
      { "countryCode": "CL", "countryName": "chile", "prefix": "+56", "flag": "🇨🇱" },
      { "countryCode": "CN", "countryName": "china", "prefix": "+86", "flag": "🇨🇳" },
      { "countryCode": "CX", "countryName": "christmas_island", "prefix": "+61", "flag": "🇨🇽" },
      { "countryCode": "CC", "countryName": "cocos_keeling_islands", "prefix": "+61", "flag": "🇨🇨" },
      { "countryCode": "CO", "countryName": "colombia", "prefix": "+57", "flag": "🇨🇴" },
      { "countryCode": "KM", "countryName": "comoros", "prefix": "+269", "flag": "🇰🇲" },
      { "countryCode": "CG", "countryName": "congo", "prefix": "+242", "flag": "🇨🇬" },
      { "countryCode": "CD", "countryName": "congo_the_democratic_republic_of_the", "prefix": "+243", "flag": "🇨🇩" },
      { "countryCode": "CK", "countryName": "cook_islands", "prefix": "+682", "flag": "🇨🇰" },
      { "countryCode": "CR", "countryName": "costa_rica", "prefix": "+506", "flag": "🇨🇷" },
      { "countryCode": "CI", "countryName": "cte_divoire", "prefix": "+225", "flag": "🇨🇮" },
      { "countryCode": "HR", "countryName": "croatia", "prefix": "+385", "flag": "🇭🇷" },
      { "countryCode": "CU", "countryName": "cuba", "prefix": "+53", "flag": "🇨🇺" },
      { "countryCode": "CY", "countryName": "cyprus", "prefix": "+357", "flag": "🇨🇾" },
      { "countryCode": "CZ", "countryName": "czech_republic", "prefix": "+420", "flag": "🇨🇿" },
      { "countryCode": "DK", "countryName": "denmark", "prefix": "+45", "flag": "🇩🇰" },
      { "countryCode": "DJ", "countryName": "djibouti", "prefix": "+253", "flag": "🇩🇯" },
      { "countryCode": "DM", "countryName": "dominica", "prefix": "+1-767", "flag": "🇩🇲" },
      // { "countryCode": "DO", "countryName": "dominican_republic", "prefix": "+1-809, +1-829, +1-849", "flag": "🇩🇴" },
      { "countryCode": "EC", "countryName": "ecuador", "prefix": "+593", "flag": "🇪🇨" },
      { "countryCode": "EG", "countryName": "egypt", "prefix": "+20", "flag": "🇪🇬" },
      { "countryCode": "SV", "countryName": "el_salvador", "prefix": "+503", "flag": "🇸🇻" },
      { "countryCode": "GQ", "countryName": "equatorial_guinea", "prefix": "+240", "flag": "🇬🇶" },
      { "countryCode": "ER", "countryName": "eritrea", "prefix": "+291", "flag": "🇪🇷" },
      { "countryCode": "EE", "countryName": "estonia", "prefix": "+372", "flag": "🇪🇪" },
      { "countryCode": "ET", "countryName": "ethiopia", "prefix": "+251", "flag": "🇪🇹" },
      { "countryCode": "FK", "countryName": "falkland_islands_malvinas", "prefix": "+500", "flag": "🇫🇰" },
      { "countryCode": "FO", "countryName": "faroe_islands", "prefix": "+298", "flag": "🇫🇴" },
      { "countryCode": "FJ", "countryName": "fiji", "prefix": "+679", "flag": "🇫🇯" },
      { "countryCode": "FI", "countryName": "finland", "prefix": "+358", "flag": "🇫🇮" },
      { "countryCode": "FR", "countryName": "france", "prefix": "+33", "flag": "🇫🇷" },
      { "countryCode": "GF", "countryName": "french_guiana", "prefix": "+594", "flag": "🇬🇫" },
      { "countryCode": "PF", "countryName": "french_polynesia", "prefix": "+689", "flag": "🇵🇫" },
    
      { "countryCode": "GA", "countryName": "gabon", "prefix": "+241", "flag": "🇬🇦" },
      { "countryCode": "GM", "countryName": "gambia", "prefix": "+220", "flag": "🇬🇲" },
      { "countryCode": "GE", "countryName": "georgia", "prefix": "+995", "flag": "🇬🇪" },
      { "countryCode": "DE", "countryName": "germany", "prefix": "+49", "flag": "🇩🇪" },
      { "countryCode": "GH", "countryName": "ghana", "prefix": "+233", "flag": "🇬🇭" },
      { "countryCode": "GI", "countryName": "gibraltar", "prefix": "+350", "flag": "🇬🇮" },
      { "countryCode": "GR", "countryName": "greece", "prefix": "+30", "flag": "🇬🇷" },
      { "countryCode": "GL", "countryName": "greenland", "prefix": "+299", "flag": "🇬🇱" },
      { "countryCode": "GD", "countryName": "grenada", "prefix": "+1-473", "flag": "🇬🇩" },
      { "countryCode": "GP", "countryName": "guadeloupe", "prefix": "+590", "flag": "🇬🇵" },
      { "countryCode": "GU", "countryName": "guam", "prefix": "+1-671", "flag": "🇬🇺" },
      { "countryCode": "GT", "countryName": "guatemala", "prefix": "+502", "flag": "🇬🇹" },
      { "countryCode": "GG", "countryName": "guernsey", "prefix": "+44", "flag": "🇬🇬" },
      { "countryCode": "GN", "countryName": "guinea", "prefix": "+224", "flag": "🇬🇳" },
      { "countryCode": "GW", "countryName": "guineabissau", "prefix": "+245", "flag": "🇬🇼" },
      { "countryCode": "GY", "countryName": "guyana", "prefix": "+592", "flag": "🇬🇾" },
      { "countryCode": "HT", "countryName": "haiti", "prefix": "+509", "flag": "🇭🇹" },
    
      { "countryCode": "VA", "countryName": "holy_see_vatican_city_state", "prefix": "+379", "flag": "🇻🇦" },
      { "countryCode": "HN", "countryName": "honduras", "prefix": "+504", "flag": "🇭🇳" },
      { "countryCode": "HK", "countryName": "hong_kong", "prefix": "+852", "flag": "🇭🇰" },
      { "countryCode": "HU", "countryName": "hungary", "prefix": "+36", "flag": "🇭🇺" },
      { "countryCode": "IS", "countryName": "iceland", "prefix": "+354", "flag": "🇮🇸" },
      { "countryCode": "IN", "countryName": "india", "prefix": "+91", "flag": "🇮🇳" },
      { "countryCode": "ID", "countryName": "indonesia", "prefix": "+62", "flag": "🇮🇩" },
      { "countryCode": "IR", "countryName": "iran_islamic_republic_of", "prefix": "+98", "flag": "🇮🇷" },
      { "countryCode": "IQ", "countryName": "iraq", "prefix": "+964", "flag": "🇮🇶" },

      { "countryCode": "IE", "countryName": "ireland", "prefix": "+353", "flag": "🇮🇪" },
      { "countryCode": "IM", "countryName": "isle_of_man", "prefix": "+44", "flag": "🇮🇲" },
      { "countryCode": "IL", "countryName": "israel", "prefix": "+972", "flag": "🇮🇱" },
      { "countryCode": "IT", "countryName": "italy", "prefix": "+39", "flag": "🇮🇹" },
      { "countryCode": "JM", "countryName": "jamaica", "prefix": "+1-876", "flag": "🇯🇲" },
      { "countryCode": "JP", "countryName": "japan", "prefix": "+81", "flag": "🇯🇵" },
      { "countryCode": "JE", "countryName": "jersey", "prefix": "+44", "flag": "🇯🇪" },
      { "countryCode": "JO", "countryName": "jordan", "prefix": "+962", "flag": "🇯🇴" },
      { "countryCode": "KZ", "countryName": "kazakhstan", "prefix": "+7", "flag": "🇰🇿" },
      { "countryCode": "KE", "countryName": "kenya", "prefix": "+254", "flag": "🇰🇪" },
      { "countryCode": "KI", "countryName": "kiribati", "prefix": "+686", "flag": "🇰🇮" },
      { "countryCode": "KP", "countryName": "korea_democratic_peoples_republic_of", "prefix": "+850", "flag": "🇰🇵" },
      { "countryCode": "KR", "countryName": "korea_republic_of", "prefix": "+82", "flag": "🇰🇷" },
      { "countryCode": "KW", "countryName": "kuwait", "prefix": "+965", "flag": "🇰🇼" },
      { "countryCode": "KG", "countryName": "kyrgyzstan", "prefix": "+996", "flag": "🇰🇬" },
      { "countryCode": "LA", "countryName": "lao_peoples_democratic_republic", "prefix": "+856", "flag": "🇱🇦" },
      { "countryCode": "LV", "countryName": "latvia", "prefix": "+371", "flag": "🇱🇻" },
      { "countryCode": "LB", "countryName": "lebanon", "prefix": "+961", "flag": "🇱🇧" },
      { "countryCode": "LS", "countryName": "lesotho", "prefix": "+266", "flag": "🇱🇸" },
      { "countryCode": "LR", "countryName": "liberia", "prefix": "+231", "flag": "🇱🇷" },
      { "countryCode": "LY", "countryName": "libyan_arab_jamahiriya", "prefix": "+218", "flag": "🇱🇾" },
      { "countryCode": "LI", "countryName": "liechtenstein", "prefix": "+423", "flag": "🇱🇮" },
      { "countryCode": "LT", "countryName": "lithuania", "prefix": "+370", "flag": "🇱🇹" },
      { "countryCode": "LU", "countryName": "luxembourg", "prefix": "+352", "flag": "🇱🇺" },
      { "countryCode": "MO", "countryName": "macao", "prefix": "+853", "flag": "🇲🇴" },
      { "countryCode": "MK", "countryName": "macedonia_the_former_yugoslav_republic_of", "prefix": "+389", "flag": "🇲🇰" },
      { "countryCode": "MG", "countryName": "madagascar", "prefix": "+261", "flag": "🇲🇬" },
      { "countryCode": "MW", "countryName": "malawi", "prefix": "+265", "flag": "🇲🇼" },
      { "countryCode": "MY", "countryName": "malaysia", "prefix": "+60", "flag": "🇲🇾" },
      { "countryCode": "MV", "countryName": "maldives", "prefix": "+960", "flag": "🇲🇻" },
      { "countryCode": "ML", "countryName": "mali", "prefix": "+223", "flag": "🇲🇱" },
      { "countryCode": "MT", "countryName": "malta", "prefix": "+356", "flag": "🇲🇹" },
      { "countryCode": "MH", "countryName": "marshall_islands", "prefix": "+692", "flag": "🇲🇭" },
      { "countryCode": "MQ", "countryName": "martinique", "prefix": "+596", "flag": "🇲🇶" },
      { "countryCode": "MR", "countryName": "mauritania", "prefix": "+222", "flag": "🇲🇷" },
      { "countryCode": "MU", "countryName": "mauritius", "prefix": "+230", "flag": "🇲🇺" },
      { "countryCode": "YT", "countryName": "mayotte", "prefix": "+262", "flag": "🇾🇹" },
      { "countryCode": "MX", "countryName": "mexico", "prefix": "+52", "flag": "🇲🇽" },
      { "countryCode": "FM", "countryName": "micronesia_federated_states_of", "prefix": "+691", "flag": "🇫🇲" },

        { "countryCode": "MD", "countryName": "moldova_republic_of", "prefix": "+373", "flag": "🇲🇩" },
        { "countryCode": "MC", "countryName": "monaco", "prefix": "+377", "flag": "🇲🇨" },
        { "countryCode": "MN", "countryName": "mongolia", "prefix": "+976", "flag": "🇲🇳" },
        { "countryCode": "ME", "countryName": "montenegro", "prefix": "+382", "flag": "🇲🇪" },
        { "countryCode": "MS", "countryName": "montserrat", "prefix": "+1-664", "flag": "🇲🇸" },
        { "countryCode": "MA", "countryName": "morocco", "prefix": "+212", "flag": "🇲🇦" },
        { "countryCode": "MZ", "countryName": "mozambique", "prefix": "+258", "flag": "🇲🇿" },
        { "countryCode": "MM", "countryName": "myanmar", "prefix": "+95", "flag": "🇲🇲" },
        { "countryCode": "NA", "countryName": "namibia", "prefix": "+264", "flag": "🇳🇦" },
        { "countryCode": "NR", "countryName": "nauru", "prefix": "+674", "flag": "🇳🇷" },
        { "countryCode": "NP", "countryName": "nepal", "prefix": "+977", "flag": "🇳🇵" },
        { "countryCode": "NL", "countryName": "netherlands", "prefix": "+31", "flag": "🇳🇱" },
        { "countryCode": "AN", "countryName": "netherlands_antilles", "prefix": "+599", "flag": "🇦🇳" },
        { "countryCode": "NC", "countryName": "new_caledonia", "prefix": "+687", "flag": "🇳🇨" },
        { "countryCode": "NZ", "countryName": "new_zealand", "prefix": "+64", "flag": "🇳🇿" },
        { "countryCode": "NI", "countryName": "nicaragua", "prefix": "+505", "flag": "🇳🇮" },
        { "countryCode": "NE", "countryName": "niger", "prefix": "+227", "flag": "🇳🇪" },
        { "countryCode": "NG", "countryName": "nigeria", "prefix": "+234", "flag": "🇳🇬" },
        { "countryCode": "NU", "countryName": "niue", "prefix": "+683", "flag": "🇳🇺" },
        { "countryCode": "NF", "countryName": "norfolk_island", "prefix": "+672", "flag": "🇳🇫" },
        { "countryCode": "MP", "countryName": "northern_mariana_islands", "prefix": "+1-670", "flag": "🇲🇵" },
        { "countryCode": "NO", "countryName": "norway", "prefix": "+47", "flag": "🇳🇴" },
        { "countryCode": "OM", "countryName": "oman", "prefix": "+968", "flag": "🇴🇲" },
        { "countryCode": "PK", "countryName": "pakistan", "prefix": "+92", "flag": "🇵🇰" },
        { "countryCode": "PW", "countryName": "palau", "prefix": "+680", "flag": "🇵🇼" },
        { "countryCode": "PS", "countryName": "palestinian_territory_occupied", "prefix": "+970", "flag": "🇵🇸" },
        { "countryCode": "PA", "countryName": "panama", "prefix": "+507", "flag": "🇵🇦" },
        { "countryCode": "PG", "countryName": "papua_new_guinea", "prefix": "+675", "flag": "🇵🇬" },
        { "countryCode": "PY", "countryName": "paraguay", "prefix": "+595", "flag": "🇵🇾" },
        { "countryCode": "PE", "countryName": "peru", "prefix": "+51", "flag": "🇵🇪" },
        { "countryCode": "PH", "countryName": "philippines", "prefix": "+63", "flag": "🇵🇭" },
        { "countryCode": "PN", "countryName": "pitcairn", "prefix": "+64", "flag": "🇵🇳" },
        { "countryCode": "PL", "countryName": "poland", "prefix": "+48", "flag": "🇵🇱" },
        { "countryCode": "PT", "countryName": "portugal", "prefix": "+351", "flag": "🇵🇹" },
        { "countryCode": "PR", "countryName": "puerto_rico", "prefix": "+1-787", "flag": "🇵🇷" },
        { "countryCode": "QA", "countryName": "qatar", "prefix": "+974", "flag": "🇶🇦" },
        { "countryCode": "RE", "countryName": "réunion", "prefix": "+262", "flag": "🇷🇪" },
        { "countryCode": "RO", "countryName": "romania", "prefix": "+40", "flag": "🇷🇴" },
        { "countryCode": "RU", "countryName": "russian_federation", "prefix": "+7", "flag": "🇷🇺" },
        { "countryCode": "RW", "countryName": "rwanda", "prefix": "+250", "flag": "🇷🇼" },

      { "countryCode": "SH", "countryName": "saint_helena_ascension_and_tristan_da_cunha", "prefix": "+290", "flag": "🇸🇭" },
      { "countryCode": "KN", "countryName": "saint_kitts_and_nevis", "prefix": "+1-869", "flag": "🇰🇳" },
      { "countryCode": "LC", "countryName": "saint_lucia", "prefix": "+1-758", "flag": "🇱🇨" },
      { "countryCode": "MF", "countryName": "saint_martin_french_part", "prefix": "+590", "flag": "🇲🇫" },
      { "countryCode": "PM", "countryName": "saint_pierre_and_miquelon", "prefix": "+508", "flag": "🇵🇲" },
      { "countryCode": "VC", "countryName": "saint_vincent_and_the_grenadines", "prefix": "+1-784", "flag": "🇻🇨" },
      { "countryCode": "WS", "countryName": "samoa", "prefix": "+685", "flag": "🇼🇸" },
      { "countryCode": "SM", "countryName": "san_marino", "prefix": "+378", "flag": "🇸🇲" },
      { "countryCode": "ST", "countryName": "sao_tome_and_principe", "prefix": "+239", "flag": "🇸🇹" },
      { "countryCode": "SA", "countryName": "saudi_arabia", "prefix": "+966", "flag": "🇸🇦" },
      { "countryCode": "SN", "countryName": "senegal", "prefix": "+221", "flag": "🇸🇳" },
      { "countryCode": "RS", "countryName": "serbia", "prefix": "+381", "flag": "🇷🇸" },
      { "countryCode": "SC", "countryName": "seychelles", "prefix": "+248", "flag": "🇸🇨" },
      { "countryCode": "SL", "countryName": "sierra_leone", "prefix": "+232", "flag": "🇸🇱" },
      { "countryCode": "SG", "countryName": "singapore", "prefix": "+65", "flag": "🇸🇬" },
      { "countryCode": "SK", "countryName": "slovakia", "prefix": "+421", "flag": "🇸🇰" },
      { "countryCode": "SI", "countryName": "slovenia", "prefix": "+386", "flag": "🇸🇮" },
      { "countryCode": "SB", "countryName": "solomon_islands", "prefix": "+677", "flag": "🇸🇧" },
      { "countryCode": "SO", "countryName": "somalia", "prefix": "+252", "flag": "🇸🇴" },
      { "countryCode": "ZA", "countryName": "south_africa", "prefix": "+27", "flag": "🇿🇦" },
      
      { "countryCode": "ES", "countryName": "spain", "prefix": "+34", "flag": "🇪🇸" },
      { "countryCode": "LK", "countryName": "sri_lanka", "prefix": "+94", "flag": "🇱🇰" },
      { "countryCode": "SD", "countryName": "sudan", "prefix": "+249", "flag": "🇸🇩" },
      { "countryCode": "SR", "countryName": "suriname", "prefix": "+597", "flag": "🇸🇷" },
      { "countryCode": "SJ", "countryName": "svalbard_and_jan_mayen", "prefix": "+47", "flag": "🇸🇯" },
      { "countryCode": "SZ", "countryName": "swaziland", "prefix": "+268", "flag": "🇸🇿" },
      { "countryCode": "SE", "countryName": "sweden", "prefix": "+46", "flag": "🇸🇪" },
      { "countryCode": "CH", "countryName": "switzerland", "prefix": "+41", "flag": "🇨🇭" },
      { "countryCode": "SY", "countryName": "syrian_arab_republic", "prefix": "+963", "flag": "🇸🇾" },

      { "countryCode": "TW", "countryName": "taiwan_province_of_china", "prefix": "+886", "flag": "🇹🇼" },
      { "countryCode": "TJ", "countryName": "tajikistan", "prefix": "+992", "flag": "🇹🇯" },
      { "countryCode": "TZ", "countryName": "tanzania_united_republic_of", "prefix": "+255", "flag": "🇹🇿" },
      { "countryCode": "TH", "countryName": "thailand", "prefix": "+66", "flag": "🇹🇭" },
      { "countryCode": "TL", "countryName": "timor-leste", "prefix": "+670", "flag": "🇹🇱" },
      { "countryCode": "TG", "countryName": "togo", "prefix": "+228", "flag": "🇹🇬" },
      { "countryCode": "TK", "countryName": "tokelau", "prefix": "+690", "flag": "🇹🇰" },
      { "countryCode": "TO", "countryName": "tonga", "prefix": "+676", "flag": "🇹🇴" },
      { "countryCode": "TT", "countryName": "trinidad_and_tobago", "prefix": "+1-868", "flag": "🇹🇹" },
      { "countryCode": "TN", "countryName": "tunisia", "prefix": "+216", "flag": "🇹🇳" },
      { "countryCode": "TR", "countryName": "turkey", "prefix": "+90", "flag": "🇹🇷" },
      { "countryCode": "TM", "countryName": "turkmenistan", "prefix": "+993", "flag": "🇹🇲" },
      { "countryCode": "TC", "countryName": "turks_and_caicos_islands", "prefix": "+1-649", "flag": "🇹🇨" },
      { "countryCode": "TV", "countryName": "tuvalu", "prefix": "+688", "flag": "🇹🇻" },
      { "countryCode": "UG", "countryName": "uganda", "prefix": "+256", "flag": "🇺🇬" },
      { "countryCode": "UA", "countryName": "ukraine", "prefix": "+380", "flag": "🇺🇦" },
      { "countryCode": "AE", "countryName": "united_arab_emirates", "prefix": "+971", "flag": "🇦🇪" },
      { "countryCode": "GB", "countryName": "united_kingdom", "prefix": "+44", "flag": "🇬🇧" },
      { "countryCode": "US", "countryName": "united_states", "prefix": "+1", "flag": "🇺🇸" },
      { "countryCode": "UY", "countryName": "uruguay", "prefix": "+598", "flag": "🇺🇾" },
      { "countryCode": "UZ", "countryName": "uzbekistan", "prefix": "+998", "flag": "🇺🇿" },
      { "countryCode": "VU", "countryName": "vanuatu", "prefix": "+678", "flag": "🇻🇺" },
      { "countryCode": "VE", "countryName": "venezuela_bolivarian_republic_of", "prefix": "+58", "flag": "🇻🇪" },
      { "countryCode": "VN", "countryName": "viet_nam", "prefix": "+84", "flag": "🇻🇳" },
      { "countryCode": "VG", "countryName": "virgin_islands_british", "prefix": "+1-284", "flag": "🇻🇬" },
      { "countryCode": "VI", "countryName": "virgin_islands_us", "prefix": "+1-340", "flag": "🇻🇮" },
      { "countryCode": "WF", "countryName": "wallis_and_futuna", "prefix": "+681", "flag": "🇼🇫" },
      { "countryCode": "YE", "countryName": "yemen", "prefix": "+967", "flag": "🇾🇪" },
      { "countryCode": "ZM", "countryName": "zambia", "prefix": "+260", "flag": "🇿🇲" },
      { "countryCode": "ZW", "countryName": "zimbabwe", "prefix": "+263", "flag": "🇿🇼" }
    ],
    TIMEZONES: ["Asia/Aden","America/Cuiaba","Etc/GMT+9","Etc/GMT+8","Africa/Nairobi","America/Marigot","Asia/Aqtau","Pacific/Kwajalein","America/El_Salvador","Asia/Pontianak","Africa/Cairo","Pacific/Pago_Pago","Africa/Mbabane","Asia/Kuching","Pacific/Honolulu","Pacific/Rarotonga","America/Guatemala","Australia/Hobart","Europe/London","America/Belize","America/Panama","Asia/Chungking","America/Managua","America/Indiana/Petersburg","Asia/Yerevan","Europe/Brussels","GMT","Europe/Warsaw","America/Chicago","Asia/Kashgar","Chile/Continental","Pacific/Yap","CET","Etc/GMT-1","Etc/GMT-0","Europe/Jersey","America/Tegucigalpa","Etc/GMT-5","Europe/Istanbul","America/Eirunepe","Etc/GMT-4","America/Miquelon","Etc/GMT-3","Europe/Luxembourg","Etc/GMT-2","Etc/GMT-9","America/Argentina/Catamarca","Etc/GMT-8","Etc/GMT-7","Etc/GMT-6","Europe/Zaporozhye","Canada/Yukon","Canada/Atlantic","Atlantic/St_Helena","Australia/Tasmania","Libya","Europe/Guernsey","America/Grand_Turk","Asia/Samarkand","America/Argentina/Cordoba","Asia/Phnom_Penh","Africa/Kigali","Asia/Almaty","US/Alaska","Asia/Dubai","Europe/Isle_of_Man","America/Araguaina","Cuba","Asia/Novosibirsk","America/Argentina/Salta","Etc/GMT+3","Africa/Tunis","Etc/GMT+2","Etc/GMT+1","Pacific/Fakaofo","Africa/Tripoli","Etc/GMT+0","Israel","Africa/Banjul","Etc/GMT+7","Indian/Comoro","Etc/GMT+6","Etc/GMT+5","Etc/GMT+4","Pacific/Port_Moresby","US/Arizona","Antarctica/Syowa","Indian/Reunion","Pacific/Palau","Europe/Kaliningrad","America/Montevideo","Africa/Windhoek","Asia/Karachi","Africa/Mogadishu","Australia/Perth","Brazil/East","Etc/GMT","Asia/Chita","Pacific/Easter","Antarctica/Davis","Antarctica/McMurdo","Asia/Macao","America/Manaus","Africa/Freetown","Europe/Bucharest","Asia/Tomsk","America/Argentina/Mendoza","Asia/Macau","Europe/Malta","Mexico/BajaSur","Pacific/Tahiti","Africa/Asmera","Europe/Busingen","America/Argentina/Rio_Gallegos","Africa/Malabo","Europe/Skopje","America/Catamarca","America/Godthab","Europe/Sarajevo","Australia/ACT","GB-Eire","Africa/Lagos","America/Cordoba","Europe/Rome","Asia/Dacca","Indian/Mauritius","Pacific/Samoa","America/Regina","America/Fort_Wayne","America/Dawson_Creek","Africa/Algiers","Europe/Mariehamn","America/St_Johns","America/St_Thomas","Europe/Zurich","America/Anguilla","Asia/Dili","America/Denver","Africa/Bamako","Europe/Saratov","GB","Mexico/General","Pacific/Wallis","Europe/Gibraltar","Africa/Conakry","Africa/Lubumbashi","Asia/Istanbul","America/Havana","NZ-CHAT","Asia/Choibalsan","America/Porto_Acre","Asia/Omsk","Europe/Vaduz","US/Michigan","Asia/Dhaka","America/Barbados","Europe/Tiraspol","Atlantic/Cape_Verde","Asia/Yekaterinburg","America/Louisville","Pacific/Johnston","Pacific/Chatham","Europe/Ljubljana","America/Sao_Paulo","Asia/Jayapura","America/Curacao","Asia/Dushanbe","America/Guyana","America/Guayaquil","America/Martinique","Portugal","Europe/Berlin","Europe/Moscow","Europe/Chisinau","America/Puerto_Rico","America/Rankin_Inlet","Pacific/Ponape","Europe/Stockholm","Europe/Budapest","America/Argentina/Jujuy","Australia/Eucla","Asia/Shanghai","Universal","Europe/Zagreb","America/Port_of_Spain","Europe/Helsinki","Asia/Beirut","Asia/Tel_Aviv","Pacific/Bougainville","US/Central","Africa/Sao_Tome","Indian/Chagos","America/Cayenne","Asia/Yakutsk","Pacific/Galapagos","Australia/North","Europe/Paris","Africa/Ndjamena","Pacific/Fiji","America/Rainy_River","Indian/Maldives","Australia/Yancowinna","SystemV/AST4","Asia/Oral","America/Yellowknife","Pacific/Enderbury","America/Juneau","Australia/Victoria","America/Indiana/Vevay","Asia/Tashkent","Asia/Jakarta","Africa/Ceuta","Asia/Barnaul","America/Recife","America/Buenos_Aires","America/Noronha","America/Swift_Current","Australia/Adelaide","America/Metlakatla","Africa/Djibouti","America/Paramaribo","Asia/Qostanay","Europe/Simferopol","Europe/Sofia","Africa/Nouakchott","Europe/Prague","America/Indiana/Vincennes","Antarctica/Mawson","America/Kralendijk","Antarctica/Troll","Europe/Samara","Indian/Christmas","America/Antigua","Pacific/Gambier","America/Indianapolis","America/Inuvik","America/Iqaluit","Pacific/Funafuti","UTC","Antarctica/Macquarie","Canada/Pacific","America/Moncton","Africa/Gaborone","Pacific/Chuuk","Asia/Pyongyang","America/St_Vincent","Asia/Gaza","Etc/Universal","PST8PDT","Atlantic/Faeroe","Asia/Qyzylorda","Canada/Newfoundland","America/Kentucky/Louisville","America/Yakutat","America/Ciudad_Juarez","Asia/Ho_Chi_Minh","Antarctica/Casey","Europe/Copenhagen","Africa/Asmara","Atlantic/Azores","Europe/Vienna","ROK","Pacific/Pitcairn","America/Mazatlan","Australia/Queensland","Pacific/Nauru","Europe/Tirane","Asia/Kolkata","SystemV/MST7","Australia/Canberra","MET","Australia/Broken_Hill","Europe/Riga","America/Dominica","Africa/Abidjan","America/Mendoza","America/Santarem","Kwajalein","America/Asuncion","Asia/Ulan_Bator","NZ","America/Boise","Australia/Currie","EST5EDT","Pacific/Guam","Pacific/Wake","Atlantic/Bermuda","America/Costa_Rica","America/Dawson","Asia/Chongqing","Eire","Europe/Amsterdam","America/Indiana/Knox","America/North_Dakota/Beulah","Africa/Accra","Atlantic/Faroe","Mexico/BajaNorte","America/Maceio","Etc/UCT","Pacific/Apia","GMT0","America/Atka","Pacific/Niue","Australia/Lord_Howe","Europe/Dublin","Pacific/Truk","MST7MDT","America/Monterrey","America/Nassau","America/Jamaica","Asia/Bishkek","America/Atikokan","Atlantic/Stanley","Australia/NSW","US/Hawaii","SystemV/CST6","Indian/Mahe","Asia/Aqtobe","America/Sitka","Asia/Vladivostok","Africa/Libreville","Africa/Maputo","Zulu","America/Kentucky/Monticello","Africa/El_Aaiun","Africa/Ouagadougou","America/Coral_Harbour","Pacific/Marquesas","Brazil/West","America/Aruba","America/North_Dakota/Center","America/Cayman","Asia/Ulaanbaatar","Asia/Baghdad","Europe/San_Marino","America/Indiana/Tell_City","America/Tijuana","Pacific/Saipan","SystemV/YST9","Africa/Douala","America/Chihuahua","America/Ojinaga","Asia/Hovd","America/Anchorage","Chile/EasterIsland","America/Halifax","Antarctica/Rothera","America/Indiana/Indianapolis","US/Mountain","Asia/Damascus","America/Argentina/San_Luis","America/Santiago","Asia/Baku","America/Argentina/Ushuaia","Atlantic/Reykjavik","Africa/Brazzaville","Africa/Porto-Novo","America/La_Paz","Antarctica/DumontDUrville","Asia/Taipei","Antarctica/South_Pole","Asia/Manila","Asia/Bangkok","Africa/Dar_es_Salaam","Poland","Atlantic/Madeira","Antarctica/Palmer","America/Thunder_Bay","Africa/Addis_Ababa","Asia/Yangon","Europe/Uzhgorod","Brazil/DeNoronha","Asia/Ashkhabad","Etc/Zulu","America/Indiana/Marengo","America/Creston","America/Punta_Arenas","America/Mexico_City","Antarctica/Vostok","Asia/Jerusalem","Europe/Andorra","US/Samoa","PRC","Asia/Vientiane","Pacific/Kiritimati","America/Matamoros","America/Blanc-Sablon","Asia/Riyadh","Iceland","Pacific/Pohnpei","Asia/Ujung_Pandang","Atlantic/South_Georgia","Europe/Lisbon","Asia/Harbin","Europe/Oslo","Asia/Novokuznetsk","CST6CDT","Atlantic/Canary","America/Knox_IN","Asia/Kuwait","SystemV/HST10","Pacific/Efate","Africa/Lome","America/Bogota","America/Menominee","America/Adak","Pacific/Norfolk","Europe/Kirov","America/Resolute","Pacific/Kanton","Pacific/Tarawa","Africa/Kampala","Asia/Krasnoyarsk","Greenwich","SystemV/EST5","America/Edmonton","Europe/Podgorica","Australia/South","Canada/Central","Africa/Bujumbura","America/Santo_Domingo","US/Eastern","Europe/Minsk","Pacific/Auckland","Africa/Casablanca","America/Glace_Bay","Canada/Eastern","Asia/Qatar","Europe/Kiev","Singapore","Asia/Magadan","SystemV/PST8","America/Port-au-Prince","Europe/Belfast","America/St_Barthelemy","Asia/Ashgabat","Africa/Luanda","America/Nipigon","Atlantic/Jan_Mayen","Brazil/Acre","Asia/Muscat","Asia/Bahrain","Europe/Vilnius","America/Fortaleza","Etc/GMT0","US/East-Indiana","America/Hermosillo","America/Cancun","Africa/Maseru","Pacific/Kosrae","Africa/Kinshasa","Asia/Kathmandu","Asia/Seoul","Australia/Sydney","America/Lima","Australia/LHI","America/St_Lucia","Europe/Madrid","America/Bahia_Banderas","America/Montserrat","Asia/Brunei","America/Santa_Isabel","Canada/Mountain","America/Cambridge_Bay","Asia/Colombo","Australia/West","Indian/Antananarivo","Australia/Brisbane","Indian/Mayotte","US/Indiana-Starke","Asia/Urumqi","US/Aleutian","Europe/Volgograd","America/Lower_Princes","America/Vancouver","Africa/Blantyre","America/Rio_Branco","America/Danmarkshavn","America/Detroit","America/Thule","Africa/Lusaka","Asia/Hong_Kong","Iran","America/Argentina/La_Rioja","Africa/Dakar","SystemV/CST6CDT","America/Tortola","America/Porto_Velho","Asia/Sakhalin","Etc/GMT+10","America/Scoresbysund","Asia/Kamchatka","Asia/Thimbu","Africa/Harare","Etc/GMT+12","Etc/GMT+11","Navajo","America/Nome","Europe/Tallinn","Turkey","Africa/Khartoum","Africa/Johannesburg","Africa/Bangui","Europe/Belgrade","Jamaica","Africa/Bissau","Asia/Tehran","WET","Europe/Astrakhan","Africa/Juba","America/Campo_Grande","America/Belem","Etc/Greenwich","Asia/Saigon","America/Ensenada","Pacific/Midway","America/Jujuy","Africa/Timbuktu","America/Bahia","America/Goose_Bay","America/Virgin","America/Pangnirtung","Asia/Katmandu","America/Phoenix","Africa/Niamey","America/Whitehorse","Pacific/Noumea","Asia/Tbilisi","Europe/Kyiv","America/Montreal","Asia/Makassar","America/Argentina/San_Juan","Hongkong","UCT","Asia/Nicosia","America/Indiana/Winamac","SystemV/MST7MDT","America/Argentina/ComodRivadavia","America/Boa_Vista","America/Grenada","Asia/Atyrau","Australia/Darwin","Asia/Khandyga","Asia/Kuala_Lumpur","Asia/Famagusta","Asia/Thimphu","Asia/Rangoon","Europe/Bratislava","Asia/Calcutta","America/Argentina/Tucuman","Asia/Kabul","Indian/Cocos","Japan","Pacific/Tongatapu","America/New_York","Etc/GMT-12","Etc/GMT-11","America/Nuuk","Etc/GMT-10","SystemV/YST9YDT","Europe/Ulyanovsk","Etc/GMT-14","Etc/GMT-13","W-SU","America/Merida","EET","America/Rosario","Canada/Saskatchewan","America/St_Kitts","Arctic/Longyearbyen","America/Fort_Nelson","America/Caracas","America/Guadeloupe","Asia/Hebron","Indian/Kerguelen",
    "SystemV/PST8PDT","Africa/Monrovia","Asia/Ust-Nera","Egypt","Asia/Srednekolymsk","America/North_Dakota/New_Salem","Asia/Anadyr","Australia/Melbourne","Asia/Irkutsk","America/Shiprock","America/Winnipeg","Europe/Vatican","Asia/Amman","Etc/UTC","SystemV/AST4ADT","Asia/Tokyo","America/Toronto","Asia/Singapore","Australia/Lindeman","America/Los_Angeles","SystemV/EST5EDT","Pacific/Majuro","America/Argentina/Buenos_Aires","Europe/Nicosia","Pacific/Guadalcanal","Europe/Athens","US/Pacific","Europe/Monaco"],
    ONBOARDING_QUESTIONS: [
      {
        type: 'intro',
        title: 'onboarding.intro.title',
        description: 'onboarding.intro.description',
        primaryButton: 'onboarding.intro.primaryButton',
        answerKey: 'intro',
      },
      {
        type: 'question',
        title: 'onboarding.question1.title',
        description: 'onboarding.question1.description',
        primaryButton: 'onboarding.question1.primaryButton',
        secondaryButton: 'onboarding.question1.secondaryButton',
        answerKey: 'calendar_preset',
      },
      {
        type: 'multiple-choice',
        title: 'onboarding.multiple_choice.title',
        description: 'onboarding.multiple_choice.description',
        options: [
          { value: 'own_production', label: 'onboarding.multiple_choice.option.own_production' },
          { value: 'other_designers', label: 'onboarding.multiple_choice.option.other_designers' },
          { value: 'second_hand', label: 'onboarding.multiple_choice.option.second_hand' }
        ],
        primaryButton: 'onboarding.multiple_choice.primaryButton',
        answerKey: 'sales_type',
      },
      {
        type: 'question',
        title: 'onboarding.website_updates.title',
        description: 'onboarding.website_updates.description',
        primaryButton: 'onboarding.website_updates.primaryButton',
        secondaryButton: 'onboarding.website_updates.secondaryButton',
        answerKey: 'website_updates',
      },
      {
        type: 'multiple-choice',
        title: 'onboarding.common_problems.title',
        description: 'onboarding.common_problems.description',
        options: [
          { value: 'no_shows_phone_calls', label: 'onboarding.common_problems.option.no_shows_phone_calls' },
          { value: 'orders_tracking', label: 'onboarding.common_problems.option.orders_tracking' },
          { value: 'social_media', label: 'onboarding.common_problems.option.social_media' },
          { value: 'finding_partners', label: 'onboarding.common_problems.option.finding_partners' }
        ],
        primaryButton: 'onboarding.common_problems.primaryButton',
        secondaryButton: 'onboarding.common_problems.secondaryButton',
        answerKey: 'common_problems',
      },
    ],
  };
  
export default CONFIG;